import { matchPath } from 'react-router';
import type { Selector } from 'reselect';
import Url from 'url-parse';

import type { ModelConfig, RootState, SelectorCreator, Slicer } from '@swf/core/store';
import { select } from '@swf/core/store';
import { canUseDOM, integrationTesting } from '@swf/core/utils';

import { FeatureFlag } from '../enums';
import { stripLocale } from '../utils/stripLocale';

import type { CMSUpdateAction } from './cms';

export interface LocationState {
  hash: string;
  host: string;
  hostname: string;
  href: string;
  origin: string;
  pathname: string;
  port: string;
  protocol: string;
  query: Url['query'];
}

interface LocationModel extends ModelConfig<LocationState> {
  reducers: typeof reducers;
  selectors: typeof selectors;
}

export const makeLocationState = (url: Url | string): LocationState => {
  const { auth, password, username, slashes, ...rest } =
    typeof url === 'string' ? new Url(url, true) : url;
  return rest;
};

const reducers = {
  'cms/update': (state: LocationState, { url }: CMSUpdateAction) =>
    url ? makeLocationState(url) : state,
  update: (_state: LocationState, payload: Url) => makeLocationState(payload)
};

const selectors = (slice: Slicer<LocationState>, createSelector: SelectorCreator) => ({
  get() {
    return slice;
  },
  getHost() {
    return slice((state) => state.host);
  },
  getHostname() {
    return slice((state) => state.hostname);
  },
  getQuery() {
    return slice((state) => state.query);
  },
  getRedirectPath() {
    return createSelector(
      slice,
      (rootState: RootState) => select.locale.get(rootState),
      (location, locale) => {
        const url = new Url(location.href, true);
        if (url.query.modal) {
          url.set('host', '');
          url.set('protocol', '');
          url.set('slashes', false);
          delete url.query.mitid;
          delete url.query.modal;

          return url.toString().replace(new RegExp(`^/${locale}(/.*)?$`), '$1');
        }
        return undefined;
      }
    );
  },
  getOrigin() {
    return slice((state) => state.origin);
  },
  getPathname() {
    return slice((state) => state.pathname);
  },
  getCookieDomain() {
    return slice((state) => state.hostname.split('.').slice(1).join('.'));
  },
  getWC2Domain() {
    return createSelector(this.getCookieDomain, (hostname) =>
      integrationTesting ? 'https://cashier.pokerstars.com' : `https://cashier.${hostname}`
    );
  },
  getAnalyticsPath(this: { stripLocale: Selector<RootState, string> }) {
    return createSelector(
      slice,
      this.stripLocale,
      (rootState: RootState) => select.modal.getCurrent(rootState),
      (state, pathname, currentModal) => {
        if (currentModal) {
          return '/modal' + currentModal.path;
        }

        // tracking handled by WizardGroup
        if (matchPath(pathname, { path: '/(signup|deposit|withdraw)' })) {
          return undefined;
        }

        return state.pathname;
      }
    );
  },
  stripLocale() {
    return createSelector(
      slice,
      (rootState: RootState, pathname: string | undefined) => pathname,
      (rootState: RootState) => select.locale.get(rootState),
      (location, pathname, locale) => stripLocale(pathname || location.pathname, locale)
    );
  },
  getLocaleURL() {
    return createSelector(
      slice,
      (rootState: RootState) => select.feature.check(rootState, FeatureFlag.ForceDefaultLocale),
      (rootState: RootState) => select.locale.get(rootState),
      ({ pathname }, forceDefaultLocale, locale) =>
        new RegExp(`^/${locale}(/.*)?$`).test(pathname) || forceDefaultLocale ? `/${locale}` : ''
    );
  },
  isEmbeddedRoute(this: { stripLocale: Selector<RootState, string> }) {
    return createSelector(this.stripLocale, (pathname) => pathname.startsWith('/embedded/'));
  },
  isEmbeddedModal(this: { stripLocale: Selector<RootState, string> }) {
    return createSelector(this.stripLocale, (pathname) => pathname.startsWith('/embedded/modal/'));
  },
  isEmbeddedPRHubModal(this: { stripLocale: Selector<RootState, string> }) {
    return createSelector(this.stripLocale, (pathname) =>
      pathname.startsWith('/embedded/modal/prhub/')
    );
  },
  isEmbeddedAccount(this: { stripLocale: Selector<RootState, string> }) {
    return createSelector(this.stripLocale, (pathname) =>
      pathname.startsWith('/embedded/account/')
    );
  },
  isEmbeddedCookieConsent(this: { stripLocale: Selector<RootState, string> }) {
    return createSelector(this.stripLocale, (pathname) =>
      pathname.startsWith('/embedded/cookie-consent/')
    );
  },
  matchEmbeddedAndModal() {
    return createSelector(
      (rootState: RootState) => select.location.getPathname(rootState),
      (rootState: RootState) => select.locale.get(rootState),
      (pathname, locale) => {
        if (new RegExp(`^(/${locale})?/embedded/modal/`).test(pathname)) {
          return '/embedded/modal';
        } else if (new RegExp(`^(/${locale})?/embedded/`).test(pathname)) {
          return '/embedded';
        }
        return '';
      }
    );
  },
  getBaseURL(this: { getLocaleURL: Selector<RootState, string> }) {
    return createSelector(
      this.getLocaleURL,
      (rootState: RootState) => select.location.getPathname(rootState),
      (rootState: RootState) => select.location.matchEmbeddedAndModal(rootState),
      (rootState: RootState) => select.locale.get(rootState),
      (localeURL, pathname, embeddedAndModal, locale) =>
        (embeddedAndModal ? localeURL + embeddedAndModal : localeURL) || undefined
    );
  },
  stripBaseURL(this: { getPathname: Selector<RootState, string> }) {
    return createSelector(
      (rootState: RootState) => select.location.getPathname(rootState),
      (rootState: RootState) => select.location.matchEmbeddedAndModal(rootState),
      (rootState: RootState) => select.locale.get(rootState),
      (pathname, embeddedAndModal, locale) =>
        pathname.replace(new RegExp(`^(/${locale})?${embeddedAndModal}(/.*)?$`), '$2')
    );
  }
});

const initialState: LocationState = canUseDOM
  ? makeLocationState(window.location.href)
  : ({ query: {} } as LocationState);

const location: LocationModel = {
  state: initialState,
  reducers,
  selectors
};

export default location;
